import { IS_LOCAL } from "@/env";
import { useEffect } from "react";

import { captureEvent, pushToDataLayer } from "../_analytics/analytics";
import { EventName, GoogleTagManagerOnlyEventName } from "@mw/shared/analyticsEventNames";
import { logger } from "@utilities/logger";
import { isGPCEnabled } from "./gpc";
import { cancelIdleCallbackWithFallback, requestIdleCallbackWithFallback } from "./requestIdle";

// Should ALWAYS be false in prod
const LOCAL_TESTING_FLAG_INDICATE_EU = false;

export type CookiebotConsent = {
  marketing: boolean;
  necessary: boolean;
  preferences: boolean;
  statistics: boolean;
};

export type CookieBot = {
  consent: CookiebotConsent;
  hasResponse: boolean;
};

declare global {
  interface Window {
    Cookiebot: {
      consent: CookiebotConsent;
      hasResponse: boolean;
      renew: () => void;
    };
  }
}

export const fetchIsUserInEU = () => {
  if (IS_LOCAL && LOCAL_TESTING_FLAG_INDICATE_EU) {
    return true;
  }
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone.includes("Europe");
  } catch (e) {
    logger.error(e instanceof Error ? e : null, "Failed to resolve EU timezone");
    return false;
  }
};

export const captureUserConsent = () => {
  if (typeof window === "undefined") {
    return;
  }

  let consentHandled = false;

  const handleConsentGiven = () => {
    if (consentHandled) return;
    window.removeEventListener("CookiebotOnConsentReady", handleConsentGiven);
    consentHandled = true;

    const consent = {
      statistics: window.Cookiebot.consent.statistics,
      marketing: window.Cookiebot.consent.marketing,
      preferences: window.Cookiebot.consent.preferences,
      necessary: window.Cookiebot.consent.necessary,
    };

    captureEvent(EventName.cookiebot_consent_recorded, {
      consent,
      gpcEnabled: isGPCEnabled(),
    });
  };

  // Check if Cookiebot already has consent
  if (window.Cookiebot?.hasResponse) {
    handleConsentGiven();
  }

  // Always set up listener for future consent events
  window.addEventListener("CookiebotOnConsentReady", handleConsentGiven);
};

// provides a trigger in GTM for the fb pixel that requires both consent and user attributes to be set (if logged in)
export const useSendPixelReadyAfterConsent = () => {
  useEffect(() => {
    let idleCallbackId: number | null = null;
    let consentHandled = false;

    const handleConsentReady = () => {
      if (consentHandled) return;
      window.removeEventListener("CookiebotOnConsentReady", handleConsentReady);
      consentHandled = true;

      idleCallbackId = requestIdleCallbackWithFallback(
        () => {
          pushToDataLayer(GoogleTagManagerOnlyEventName.ready_for_pixels);
        },
        { timeout: 2000 },
      );
    };

    // Check if Cookiebot already has consent
    if (window.Cookiebot?.hasResponse) {
      handleConsentReady();
    }

    // Always set up listener for future consent events
    window.addEventListener("CookiebotOnConsentReady", handleConsentReady);

    return () => {
      window.removeEventListener("CookiebotOnConsentReady", handleConsentReady);
      if (idleCallbackId !== null) {
        cancelIdleCallbackWithFallback(idleCallbackId);
      }
    };
  }, []);
};
